import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  useToast,
  VStack,
  Link,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import Lottie from "react-lottie";
import lightOverlayAnimationData from "../assets/loginFormBgLight.json";
import darkOverlayAnimationData from "../assets/loginFormBgDark.json";
import lightAnimationData from "../assets/BgLight.json";
import darkAnimationData from "../assets/BgDark.json";
import { ToggleColorModeButton } from "../theme/ToggleColorModeButton";
import { LoadingAnimation } from "./LoadingAnimation";
import { passwordResetAPI } from "../APIs/passwordResetAPI";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const { colorMode } = useColorMode();

  const textColor = useColorModeValue("gray.800", "gray.100");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter your email address.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Call the password reset API
      await passwordResetAPI.requestPasswordReset(email.trim());
      
      setIsSubmitted(true);
      toast({
        title: "Reset Link Sent",
        description: "If an account with that email exists, we've sent a password reset link.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Password reset error:", error);
      
      // Don't reveal if the email exists or not for security
      toast({
        title: "Reset Link Sent",
        description: "If an account with that email exists, we've sent a password reset link.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
      // Still show success message for security reasons
      setIsSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const baseAnimationData =
    colorMode === "light" ? lightAnimationData : darkAnimationData;
  const overlayAnimationData =
    colorMode === "light"
      ? lightOverlayAnimationData
      : darkOverlayAnimationData;

  const baseOptions = {
    loop: false,
    autoplay: true,
    animationData: baseAnimationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const overlayOptions = {
    loop: false,
    autoplay: true,
    animationData: overlayAnimationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid meet",
    },
  };

  if (isLoading) {
    return (
      <Box position="relative" height="100vh" width="100vw">
        <LoadingAnimation size="large" />
      </Box>
    );
  }

  return (
    <Box position="relative" height="100vh" width="100vw">
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        overflow="hidden"
        pointerEvents={"none"}
      >
        <Box position="absolute" width="full" height="full">
          <Lottie options={baseOptions} />
        </Box>
        <Box position="absolute" width="full" height="full">
          <Lottie options={overlayOptions} />
        </Box>
      </Box>
      
      <ToggleColorModeButton
        width="24px"
        height="24px"
        iconButtonProps={{
          position: "absolute",
          top: "5",
          right: "5",
        }}
      />

      <VStack
        align="stretch"
        spacing={4}
        p={8}
        rounded="lg"
        maxWidth="400px"
        margin="auto"
        position="relative"
        top="50%"
        transform="translateY(-50%)"
        color={textColor}
      >
        {!isSubmitted ? (
          <>
            <Text fontSize="2xl" fontWeight="bold" alignSelf="center">
              Forgot Password
            </Text>
            <Text textAlign="center" color="gray.600" fontSize="sm">
              Enter your email address and we'll send you a link to reset your password.
            </Text>
            
            <form onSubmit={handleSubmit}>
              <VStack spacing={4} align="stretch">
                <FormControl id="email" isRequired>
                  <FormLabel>Email Address</FormLabel>
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    variant="filled"
                    borderRadius="full"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </FormControl>
                
                <Button
                  type="submit"
                  colorScheme="blue"
                  w="full"
                  p={4}
                  borderRadius="full"
                  fontSize="md"
                  fontWeight="bold"
                  boxShadow="md"
                  _hover={{ boxShadow: "lg" }}
                  isLoading={isLoading}
                  loadingText="Sending"
                >
                  Send Reset Link
                </Button>
              </VStack>
            </form>
          </>
        ) : (
          <>
            <Text fontSize="2xl" fontWeight="bold" alignSelf="center" color="green.500">
              Check Your Email
            </Text>
            <Text textAlign="center" color="gray.600" fontSize="sm">
              We've sent a password reset link to <strong>{email}</strong>
            </Text>
            <Text textAlign="center" color="gray.500" fontSize="xs">
              If you don't see the email, check your spam folder.
            </Text>
          </>
        )}
        
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          onClick={handleBackToLogin}
          alignSelf="center"
          color="blue.500"
          _hover={{ bg: "blue.50" }}
        >
          Back to Login
        </Button>
      </VStack>
    </Box>
  );
};

export default ForgotPasswordPage;
